import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Sparkles, Loader2, Users, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";


const TYPES = [
  { key: "hygienecheck",    label: "Hygiënecontrole" },
  { key: "schoonmaak",      label: "Schoonmaakrooster" },
  { key: "temperatuur",     label: "Temperatuurregistratie" },
  { key: "bereiding",       label: "Bereiding temperaturen" },
  { key: "ingangscontrole", label: "Ingangscontrole" },
];

function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <button onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      data-testid={testid}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${checked ? "bg-[#5BC044]" : "bg-zinc-300"} disabled:opacity-50`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

export default function AutoFillSettings() {
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState({});
  const [q, setQ] = useState("");
  const [shiftPersonnel, setShiftPersonnel] = useState(null); // {restaurant_id, restaurant_name}

  const load = async () => {
    const r = await api.get("/platform/auto_fill_settings");
    setRows(r.data);
  };
  useEffect(() => { load(); }, []);

  const toggle = async (row, type, next) => {
    const key = `${row.restaurant_id}:${type}`;
    setSaving(s => ({ ...s, [key]: true }));
    // Optimistic update
    setRows(rs => rs.map(x => x.restaurant_id === row.restaurant_id ? { ...x, [type]: next } : x));
    try {
      await api.put("/platform/auto_fill_settings", { restaurant_id: row.restaurant_id, [type]: next });
      toast.success(`${TYPES.find(t => t.key === type)?.label || type} · ${next ? "aan" : "uit"} voor ${row.restaurant_name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
      setRows(rs => rs.map(x => x.restaurant_id === row.restaurant_id ? { ...x, [type]: !next } : x));
    } finally {
      setSaving(s => { const c = { ...s }; delete c[key]; return c; });
    }
  };

  const filtered = (rows || []).filter(r =>
    !q.trim() ||
    r.restaurant_name.toLowerCase().includes(q.toLowerCase()) ||
    (r.organization_name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-[#0F1730] text-white">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/platform" className="inline-flex items-center gap-2 text-xs text-white/60 hover:text-white mb-2">
              <ArrowLeft size={13} /> Terug naar dashboard
            </Link>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Sparkles size={22} className="text-[#5BC044]" /> Automatisch aanvullen — instellingen
            </h1>
            <p className="text-xs text-white/60 mt-1">Zet per restaurant en per document aan of het systeem de lijsten zelf mag invullen. Uit-gezet betekent dat de chef alle rijen zelf moet invullen.</p>
          </div>
          <input
            value={q} onChange={e => setQ(e.target.value)}
            placeholder="Zoek restaurant of organisatie…"
            className="w-72 bg-white/5 border border-white/10 rounded-md h-9 px-3 text-sm placeholder-white/40"
            data-testid="auto-fill-search"
          />
        </div>

        {rows === null ? (
          <div className="flex items-center justify-center py-16 text-white/60">
            <Loader2 className="animate-spin mr-2" size={16} /> Laden…
          </div>
        ) : (
          <div className="bg-white/[0.03] border border-white/10 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/5 border-b border-white/10">
                  <th className="text-left px-4 py-3 font-semibold">Restaurant</th>
                  <th className="text-left px-4 py-3 font-semibold w-40">Organisatie</th>
                  {TYPES.map(t => (
                    <th key={t.key} className="text-center px-2 py-3 font-medium text-xs text-white/70">{t.label}</th>
                  ))}
                  <th className="text-center px-2 py-3 font-medium text-xs text-white/70">Shift-personeel</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(row => (
                  <tr key={row.restaurant_id}
                    className="border-b border-white/5 hover:bg-white/[0.02]"
                    data-testid={`auto-fill-row-${row.restaurant_id}`}>
                    <td className="px-4 py-3 font-medium">{row.restaurant_name}</td>
                    <td className="px-4 py-3 text-white/60 text-xs">{row.organization_name || "—"}</td>
                    {TYPES.map(t => {
                      const busy = !!saving[`${row.restaurant_id}:${t.key}`];
                      return (
                        <td key={t.key} className="px-2 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Toggle
                              checked={!!row[t.key]}
                              onChange={next => toggle(row, t.key, next)}
                              disabled={busy}
                              testid={`toggle-${row.restaurant_id}-${t.key}`}
                            />
                            {busy && <Loader2 size={12} className="animate-spin text-white/40" />}
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-2 py-3 text-center">
                      <button onClick={() => setShiftPersonnel({ restaurant_id: row.restaurant_id, restaurant_name: row.restaurant_name })}
                        className="inline-flex items-center gap-1.5 h-7 px-2.5 border border-white/15 hover:border-[#5BC044] text-xs text-white/80 hover:text-white rounded"
                        data-testid={`shift-personnel-${row.restaurant_id}`}>
                        <Users size={12} /> Personeel
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-white/50">Geen restaurants gevonden</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {shiftPersonnel && (
        <ShiftPersonnelModal
          restaurantId={shiftPersonnel.restaurant_id}
          restaurantName={shiftPersonnel.restaurant_name}
          onClose={() => setShiftPersonnel(null)}
        />
      )}
    </div>
  );
}

function ShiftPersonnelModal({ restaurantId, restaurantName, onClose }) {
  const [emps, setEmps] = useState(null);
  const [saving, setSaving] = useState({});

  const load = () => api.get(`/shifts/employee_settings?restaurant_id=${restaurantId}`)
    .then(r => setEmps(r.data || []))
    .catch(() => setEmps([]));
  useEffect(load, [restaurantId]);

  const toggle = async (emp, next) => {
    setSaving(s => ({ ...s, [emp.employee_id]: true }));
    setEmps(es => es.map(e => e.employee_id === emp.employee_id ? { ...e, auto_fill: next } : e));
    try {
      await api.put("/shifts/employee_settings", { restaurant_id: restaurantId, employee_id: emp.employee_id, auto_fill: next });
      toast.success(`${emp.name} · ${next ? "aan" : "uit"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
      setEmps(es => es.map(x => x.employee_id === emp.employee_id ? { ...x, auto_fill: !next } : x));
    } finally {
      setSaving(s => { const c = { ...s }; delete c[emp.employee_id]; return c; });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="shift-personnel-modal">
      <div className="bg-[#0F1730] border border-white/10 rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col text-white" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div>
            <div className="font-bold text-base">Vast rooster per personeelslid</div>
            <div className="text-xs text-white/60">{restaurantName} · zet aan voor medewerkers waarvan het rooster mag worden overgenomen uit vorige weken.</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded hover:bg-white/10 inline-flex items-center justify-center"><X size={16} /></button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {emps === null ? (
            <div className="text-center py-8 text-white/60">
              <Loader2 className="mx-auto animate-spin" size={16} />
            </div>
          ) : emps.length === 0 ? (
            <div className="text-center py-8 text-white/50 text-sm">Geen personeel gekoppeld aan dit restaurant.</div>
          ) : (
            <div className="space-y-1.5">
              {emps.map(e => (
                <div key={e.employee_id}
                  className="flex items-center justify-between bg-white/[0.03] border border-white/10 rounded px-3 py-2"
                  data-testid={`personnel-row-${e.employee_id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{e.name}</div>
                    <div className="text-[10px] text-white/50 uppercase">
                      {e.external ? "Zonder account" : e.role} {e.function ? `· ${e.function}` : ""}
                    </div>
                  </div>
                  <button onClick={() => toggle(e, !e.auto_fill)}
                    disabled={!!saving[e.employee_id]}
                    className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors shrink-0 ${e.auto_fill ? "bg-[#5BC044]" : "bg-zinc-500/40"} disabled:opacity-50`}
                    data-testid={`personnel-toggle-${e.employee_id}`}>
                    <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${e.auto_fill ? "translate-x-5" : "translate-x-1"}`} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
